import { useState } from 'react';
import { Banner, Button, Card, Dialog, Field, PageHeader, Pill } from '@elysian/ui';
import type { NewInvitation, Role } from '../../data/source';
import { useAsync } from '../../data/useAsync';
import { useSession } from '../../state/session';
import { useToast } from '../../state/toast';
import { shortDate } from '../invoices/shared';

export const ROLES: Record<Role, { label: string; can: string }> = {
  owner: { label: 'Owner', can: 'Everything, including users and backups' },
  admin: { label: 'Admin', can: 'Everything except removing the owner' },
  accountant: { label: 'Accountant', can: 'Posts journals, files VAT, closes the year' },
  bookkeeper: { label: 'Bookkeeper', can: 'Invoices, bills and the bank; no VAT filing or year end' },
  viewer: { label: 'Viewer', can: 'Sees everything, changes nothing' },
};

export function Users() {
  const { source, can } = useSession();
  const toast = useToast((s) => s.show);
  const [version, setVersion] = useState(0);
  const data = useAsync(() => source.members(), [source, version], { keep: true });
  const [inviting, setInviting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const manage = can('users.manage');

  async function revoke(id: string, email: string) {
    setError(null);
    try {
      await source.revokeInvitation(id);
      toast(`Invitation to ${email} withdrawn`);
      setVersion((v) => v + 1);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <>
      <PageHeader title="Users and access" subtitle={data.state === 'ready' ? `${data.data.billable_seats} paid ${data.data.billable_seats === 1 ? 'seat' : 'seats'}; your accountant is free` : 'Who can see and change the books'}
        actions={manage ? <Button variant="primary" onClick={() => setInviting(true)}>Invite someone</Button> : undefined} />
      {data.state === 'error' && <Banner>Couldn’t load: {data.error.message}</Banner>}
      {error && <div className="el-error-box" role="alert" style={{ marginBottom: 16 }}>{error}</div>}
      <Card as="section" padding="18px 0 8px" aria-labelledby="u-people">
        <div className="el-card-head"><h2 id="u-people" className="el-h2">People</h2></div>
        <div className="el-table-wrap">
          <table className="el-table" aria-label="People with access">
            <tbody>
              {data.state === 'ready' && data.data.members.map((m) => (
                <tr key={m.subject}>
                  <td><strong>{m.name ?? m.email}</strong><div className="el-hint">{m.email}{m.firm && ` · ${m.firm}`}</div></td>
                  <td><Pill tone={m.role === 'owner' ? 'ok' : 'info'}>{ROLES[m.role].label}</Pill><div className="el-hint">{ROLES[m.role].can}</div></td>
                  <td className="el-hint">{m.access_ends_at ? `Access ends ${shortDate(m.access_ends_at.slice(0, 10))}` : ''}{!m.billable && <div>Not charged</div>}</td>
                  <td className="el-num el-hint">{m.last_seen_at ? `Last in ${shortDate(m.last_seen_at.slice(0, 10))}` : 'Not signed in yet'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      {data.state === 'ready' && data.data.invitations.length > 0 && (
        <Card as="section" padding="18px 0 8px" aria-labelledby="u-inv" className="el-mt">
          <div className="el-card-head"><h2 id="u-inv" className="el-h2">Invitations not yet accepted</h2></div>
          <div className="el-table-wrap">
            <table className="el-table" aria-label="Invitations">
              <tbody>
                {data.data.invitations.map((i) => (
                  <tr key={i.id}>
                    <td>{i.email}</td>
                    <td><Pill tone="draft">{ROLES[i.role].label}</Pill></td>
                    <td className="el-hint">Expires {shortDate(i.expires_at.slice(0, 10))}</td>
                    <td className="el-num">{manage && <Button size="sm" variant="ghost" onClick={() => void revoke(i.id, i.email)}>Withdraw</Button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
      {inviting && <Invite onClose={() => setInviting(false)} onSent={(email, sent, url) => {
        setInviting(false);
        toast(sent ? `Invitation emailed to ${email}` : `Invitation made. Email isn’t set up, so send ${email} this link: ${url}`);
        setVersion((v) => v + 1);
      }} />}
    </>
  );
}

function Invite({ onClose, onSent }: { onClose: () => void; onSent: (email: string, sent: boolean, url?: string | null) => void }) {
  const { source } = useSession();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Exclude<Role, 'owner'>>('bookkeeper');
  const [firm, setFirm] = useState('');
  const [ends, setEnds] = useState<NonNullable<NewInvitation['access_ends']>>('never');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function send() {
    setBusy(true);
    setError(null);
    try {
      const r = await source.invite({ email: email.trim(), role, firm: firm.trim() || undefined, access_ends: role === 'accountant' ? ends : 'never', notify_owner: false });
      onSent(email.trim(), r.email_sent, r.accept_url);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }
  return (
    <Dialog title="Invite someone" onClose={onClose}
      actions={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button variant="primary" disabled={busy || !email.trim()} onClick={() => void send()}>Send invitation</Button></>}>
      <Field id="inv-email" label="Email"><input id="inv-email" type="email" className="el-input" value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
      <Field id="inv-role" label="Role" hint={ROLES[role].can}>
        <select id="inv-role" className="el-input" value={role} onChange={(e) => setRole(e.target.value as typeof role)}>
          {(['admin', 'accountant', 'bookkeeper', 'viewer'] as const).map((r) => <option key={r} value={r}>{ROLES[r].label}</option>)}
        </select>
      </Field>
      {role === 'accountant' && (
        <>
          <Field id="inv-firm" label="Firm"><input id="inv-firm" className="el-input" value={firm} onChange={(e) => setFirm(e.target.value)} /></Field>
          <Field id="inv-ends" label="Access ends">
            <select id="inv-ends" className="el-input" value={ends} onChange={(e) => setEnds(e.target.value as typeof ends)}>
              <option value="never">Never</option><option value="after_year_end">After the year end is closed</option>
            </select>
          </Field>
        </>
      )}
      <p className="el-hint" style={{ margin: 0 }}>The invitation link works for 7 days.</p>
      {error && <div className="el-error-box" role="alert">{error}</div>}
    </Dialog>
  );
}
